import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import List from '@mui/material/List';
import { hasSelectedNavigationChildren, isPageItemSelected, getPageItemFullPath } from '@cbridge-utils/navigation';
import { NavigationDividerItem, NavigationHeaderItem, NavigationLinkItem } from '@cbridge-tools/navigation-item';
import { IDashboardSidebarSubNavigationProps } from './DashboardSidebarSubNavigation.interface';

function DashboardSidebarSubNavigation({
  items,
  variant = 'primary',
  basePath = '',
  depth = 0,
  isMini = false,
  isFullyExpanded = true,
  hasDrawerTransitions = false,
}: IDashboardSidebarSubNavigationProps) {
  const { pathname } = useLocation();

  const initialExpandedSidebarItemIds = items
    .map((navigationItem, navigationItemIndex) => ({
      navigationItem,
      originalIndex: navigationItemIndex,
    }))
    .filter(({ navigationItem }) =>
      hasSelectedNavigationChildren(navigationItem, basePath, pathname),
    )
    .map(({ originalIndex }) => `${depth}-${originalIndex}`);

  const [expandedSidebarItemIds, setExpandedSidebarItemIds] = useState(initialExpandedSidebarItemIds);

  const handleOpenFolderClick = (itemId: string) => () => {
    setExpandedSidebarItemIds((previousValue) =>
      previousValue.includes(itemId)
        ? previousValue.filter(
          (previousValueItemId) => previousValueItemId !== itemId,
        )
        : [...previousValue, itemId],
    );
  };

  return (
    <List sx={{ padding: 0, mb: depth === 0 ? 0 : 1 }}>
      {items.map((navigationItem, navigationItemIndex) => {
        const navigationItemId = `${depth}-${navigationItemIndex}`;

        if (navigationItem.kind === 'header') {
          return (
            <NavigationHeaderItem
              key={`subheader-${navigationItemId}`}
              navigationItem={navigationItem}
              hasDrawerTransitions={hasDrawerTransitions}
              isFullyExpanded={isFullyExpanded}
            />
          );
        }

        if (navigationItem.kind === 'divider') {
          const nextItem = items[navigationItemIndex + 1];

          return (
            <NavigationDividerItem
              key={`divider-${navigationItemId}`}
              hasBottomMargin={nextItem?.kind !== 'header' || isMini}
              hasDrawerTransitions={hasDrawerTransitions}
              isFullyExpanded={isFullyExpanded}
            />
          );
        }

        return (
          <NavigationLinkItem
            key={navigationItemId}
            navigationItem={navigationItem}
            fullPath={getPageItemFullPath(basePath, navigationItem)}
            isNestedNavigationExpanded={expandedSidebarItemIds.includes(navigationItemId)}
            isSelected={isPageItemSelected(navigationItem, basePath, pathname)}
            depth={depth}
            isMini={isMini}
            isFullyExpanded={isFullyExpanded}
            toggleExpand={handleOpenFolderClick(navigationItemId)}
            variant={variant}
            SubNavigationComponent={DashboardSidebarSubNavigation}
          />
        );
      })}
    </List>
  );
}

export { DashboardSidebarSubNavigation };
